/* global SideDrawer */

if (!customElements.get('quick-add-drawer')) {
  class QuickAddDrawer extends SideDrawer {
    constructor() {
      super();
      this.content = this.querySelector('.js-product-details');
      this.footer = this.querySelector('.drawer__footer');
      this.form = this.querySelector('product-form');
      this.notification = this.querySelector('.js-added-to-cart');
      this.backBtn = this.querySelector('.drawer__back-btn');
      this.openCartDrawerLinks = this.querySelectorAll('.js-open-cart-drawer');
      this.cartDrawer = document.querySelector('cart-drawer');
      this.fetch = null;
      this.fetchedUrls = [];
      this.quickAddButtonMouseEnterHandler = this.handleQuickAddButtonMouseEnter.bind(this);
      this.documentClickHandler = this.handleDocumentClick.bind(this);

      document.addEventListener('click', this.documentClickHandler);
      this.addEventListener('on:variant:change', this.handleVariantChange.bind(this));

      this.openCartDrawerLinks.forEach((link) => {
        link.addEventListener('click', this.handleOpenCartClick.bind(this));
      });

      if (theme.device.hasHover && theme.mediaMatches.md) {
        document.querySelectorAll('.js-quick-add').forEach((button) => {
          this.bindQuickAddButtonMouseEnter(button);
        });

        if ('MutationObserver' in window) {
          this.observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
              // Add event listener to new buttons
              mutation.addedNodes.forEach((node) => {
                if (node.nodeType === Node.ELEMENT_NODE) {
                  node.querySelectorAll('.js-quick-add').forEach((button) => {
                    this.bindQuickAddButtonMouseEnter(button);
                  });
                }
              });

              // Remove event listener from removed buttons
              mutation.removedNodes.forEach((node) => {
                if (node.nodeType === Node.ELEMENT_NODE) {
                  node.querySelectorAll('.js-quick-add').forEach((button) => {
                    button.removeEventListener('mouseenter', this.quickAddButtonMouseEnterHandler);
                  });
                }
              });
            });
          });

          // Start observing changes to the DOM
          this.observer.observe(document.body, { childList: true, subtree: true });
        }
      }
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.documentClickHandler);
      document.querySelectorAll('.js-quick-add').forEach((button) => {
        button.removeEventListener('mouseenter', this.quickAddButtonMouseEnterHandler);
      });
      if (this.observer) this.observer.disconnect();
    }

    /**
     * Bind a mouseenter event to a given quick add button if it's not already bound
     * @param {Element} button - The button element to bind the mouseenter event to.
     */
    bindQuickAddButtonMouseEnter(button) {
      if (!button.dataset.quickAddListenerAdded) {
        button.dataset.quickAddListenerAdded = 'true';
        button.addEventListener('mouseenter', this.quickAddButtonMouseEnterHandler);
      }
    }

    /**
     * Start the fetch on hover of a quick add button for perceived performance gains
     * @param {object} evt - Event object.
     */
    handleQuickAddButtonMouseEnter(evt) {
      if (!this.fetchedUrls.includes(evt.target.dataset.productUrl)) {
        this.fetch = {
          url: evt.target.dataset.productUrl,
          promise: fetch(evt.target.dataset.productUrl)
        };
        this.fetchedUrls.push(evt.target.dataset.productUrl);
      }
    }

    /**
     * Handles 'click' events on success notification's 'View cart' link
     * @param {object} evt - Event object.
     */
    handleOpenCartClick(evt) {
      // Open the cart drawer if available on the page
      if (this.cartDrawer) {
        evt.preventDefault();
        this.cartDrawer.open();
      } else if (window.location.pathname === theme.routes.cart) {
        evt.preventDefault();
        this.close();
      }
    }

    /**
     * Handles 'click' events on the document.
     * @param {object} evt - Event object.
     */
    handleDocumentClick(evt) {
      if (!evt.target.matches('.js-quick-add')) return;

      // Close the cart drawer if it's open
      if (this.cartDrawer && this.cartDrawer.ariaHidden === 'false') {
        const overlay = document.querySelector('.js-overlay.is-visible');
        if (overlay) overlay.style.transitionDelay = '200ms';

        this.cartDrawer.close();

        // Wait a few ms for a more optimal ux/animation
        setTimeout(() => {
          this.backBtn.hidden = false;
          this.open(evt.target);
          if (overlay) overlay.style.transitionDelay = '';
        }, 200);
      } else {
        this.open(evt.target);
      }
    }

    /**
     * Handles 'on:variant:change' events on the Quick Add drawer.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      let url = this.productUrl;

      if (evt.detail.variant) {
        const separator = this.productUrl.split('?').length > 1 ? '&' : '?';
        url += `${separator}variant=${evt.detail.variant.id}`;
      }
      this.querySelectorAll('.js-prod-link').forEach((link) => {
        link.href = url;
      });
    }

    /**
     * Opens the drawer and fetches the product details.
     * @param {Element} opener - Element that triggered opening of the drawer.
     */
    async open(opener) {
      opener.setAttribute('aria-disabled', 'true');

      if (this.notification) this.notification.hidden = true;

      // If it's the same product as previously shown, there's no need to re-fetch the details.
      if (this.productUrl && this.productUrl === opener.dataset.productUrl) {
        super.open(opener);
        if (opener.dataset.selectedColor) this.setActiveVariant(opener);
        opener.removeAttribute('aria-disabled');
        return;
      }

      this.productUrl = opener.dataset.productUrl;
      this.content.innerHTML = '';
      this.classList.add('is-loading');
      this.content.classList.add('drawer__content--out');
      this.footer.classList.add('drawer__footer--out');

      super.open(opener);

      if (!this.fetch || this.fetch.url !== opener.dataset.productUrl) {
        this.fetch = {
          url: opener.dataset.productUrl,
          promise: fetch(opener.dataset.productUrl)
        };
      }

      const response = await this.fetch.promise;
      if (response.ok) {
        const tmpl = document.createElement('template');
        tmpl.innerHTML = await response.text();
        this.productEl = tmpl.content.querySelector('.js-product');
        this.renderProduct(opener);
      }

      
      
      this.fetch = null;

      opener.removeAttribute('aria-disabled');
    }

    /**
     * Closes the cart drawer.
     */
    close() {
      super.close(() => {
        this.backBtn.hidden = true;
      });
    }

    /**
     * Renders the product details.
     * @param {Element} opener - Element that triggered opening of the drawer.
     */
    renderProduct(opener) {
      // Replace instances of section id to prevent duplicates on the product page.
      const sectionId = this.productEl.dataset.section;
      this.productEl.innerHTML = this.productEl.innerHTML.replaceAll(sectionId, 'quickadd');

      // Prevent variant picker from updating the URL on change.
      const variantPicker = this.productEl.querySelector('variant-picker');
      if (variantPicker) variantPicker.dataset.updateUrl = 'false';

      // Remove size chart modal and link (if they exist).
      const sizeChartModal = this.productEl.querySelector('[data-modal="size-chart"]');
      if (sizeChartModal) {
        sizeChartModal.remove();
      }

      this.updateContent();
      this.updateForm();

      // Update the product media carousel - delay to ensure DOM is ready
      setTimeout(() => {
        this.updateMediaCarousel();
      }, 100);

      // Listen for variant changes to update carousel
      this.variantChangeHandler = this.handleVariantChangeForCarousel.bind(this);
      this.addEventListener('on:variant:change', this.variantChangeHandler);
      
      // Also listen directly on variant picker and document for variant changes
      setTimeout(() => {
        const variantPicker = this.querySelector('variant-picker');
        if (variantPicker) {
          variantPicker.addEventListener('on:variant:change', this.variantChangeHandler);
          
          // Also listen for mutations on variant picker to catch changes
          const observer = new MutationObserver(() => {
            if (variantPicker.variant) {
              setTimeout(() => {
                this.updateCarouselForVariant(variantPicker.variant);
              }, 100);
            }
          });
          observer.observe(variantPicker, { attributes: true, childList: true, subtree: true });
          this.variantObserver = observer;
        }
        
        // Also listen to variant option changes directly (swatches/clicks)
        const variantInputs = this.querySelectorAll('.opt-btn, .js-option, .opt-label');
        variantInputs.forEach(input => {
          // Listen to clicks on labels
          input.addEventListener('click', (e) => {
            setTimeout(() => {
              const picker = e.target.closest('variant-picker') || this.querySelector('variant-picker');
              if (picker && picker.variant) {
                console.log('Quick Add: Variant changed via click', picker.variant);
                this.updateCarouselForVariant(picker.variant);
              }
            }, 150);
          });
          
          // Listen to change events on inputs
          input.addEventListener('change', (e) => {
            setTimeout(() => {
              const picker = e.target.closest('variant-picker') || this.querySelector('variant-picker');
              if (picker && picker.variant) {
                console.log('Quick Add: Variant changed via input', picker.variant);
                this.updateCarouselForVariant(picker.variant);
              }
            }, 150);
          });
        });
      }, 300);

      if (opener.dataset.selectedColor) {
        // Timeout to allow the VariantPicker to initialize
        setTimeout(this.setActiveVariant.bind(this, opener), 10);
      }

     

      // $(document).ready(() => {
    
      // //   $('.qs-thumb ul.media-thumbs').slick({
      // //     slidesToShow: 3,
      // //     slidesToScroll: 1,
      // //     vertical: true, // Set to true for vertical orientation
      // //     verticalSwiping: true, // Enable vertical swiping
      // //     centerMode: true,
      // //     prevArrow: '<button type="button" class="slick-prev"><img src="https://cdn.shopify.com/s/files/1/0565/7106/8525/files/n-up.png?v=1705413631" alt="Previous"></button>',
      // //     nextArrow: '<button type="button" class="slick-next"><img src="https://cdn.shopify.com/s/files/1/0565/7106/8525/files/n-down.png?v=1705413630" alt="Next"></button>',
      // //     variableWidth: true, // Set to true for variable width
      // //     variableHeight: true, // Set to true for variable height
      // //     infinite:false,
      // //     arrows:false
      // // });

      // //   // Capture thumbnail click event
      // //   $('.qs-thumb ul.media-thumbs li.media-thumbs__item').on('click', function(){
      // //     $('.quick-add-info__media img:first').removeAttr('srcset');
      // //     $('.quick-add-info__media img:first').attr('src',$(this).find('img:first').attr('src').split('?')[0]);
      // //   });

      // //   $('.slick-next').on('click', function() {
      // //     // Top Minus | Click Down
      // //     var minTop = -($('.qs-thumb .slick-track').height() - $('.qs-thumb').height());
      // //     var currentTop = parseInt($('.qs-thumb .slick-track').css('top'));
      // //     if (currentTop -150 > minTop) {
      // //       $('.qs-thumb .slick-track').animate({
      // //           top: currentTop - 150 + 'px'
      // //       });
      // //     }
      // //   });

      // //   $('.slick-prev').on('click', function() {
      // //     // Top Plus | Click UP
      // //     var maxTop = 0;
      // //     var currentTop = parseInt($('.qs-thumb .slick-track').css('top'));
      // //     if (currentTop < maxTop) {
      // //       $('.qs-thumb .slick-track').animate({
      // //         top: currentTop + 150 + 'px'
      // //       });
      // //     }else{
      // //       $('.qs-thumb .slick-track').animate({
      // //         top: '0px'
      // //       });
      // //     }
      // //   });

      // // });
      
    }

    /**
     * Set color variant to match the one selected in the card.
     * @param {Element} opener - Element that triggered opening of the drawer.
     */
    setActiveVariant(opener) {
      const colorOptionBox = this.querySelector(`.opt-btn[value="${opener.dataset.selectedColor}"]`);
      if (colorOptionBox) {
        this.querySelector(`.opt-btn[value="${opener.dataset.selectedColor}"]`)
          .click();
      } else {
        const colorOptionDropdown = this.querySelector(
          `.custom-select__option[data-value="${opener.dataset.selectedColor}"]`
        );
        if (colorOptionDropdown) {
          const customSelect = colorOptionDropdown.closest('custom-select');
          customSelect.selectOption(colorOptionDropdown);
        }
      }
    }

    /**
     * Updates the product media carousel to match PDP mobile carousel.
     */
    updateMediaCarousel() {
      const mediaViewer = this.querySelector('.quick-add-media-viewer');
      const dotsContainer = this.querySelector('.quick-add-dots-btn');
      if (!mediaViewer) {
        console.log('Quick Add: Media viewer not found');
        return;
      }

      // Get all media items from the product element - check multiple possible locations
      let allMediaItems = this.productEl.querySelectorAll('.media-viewer__item');
      
      // If not found, try to find media gallery first
      if (allMediaItems.length === 0) {
        const mediaGallery = this.productEl.querySelector('media-gallery');
        if (mediaGallery) {
          allMediaItems = mediaGallery.querySelectorAll('.media-viewer__item');
        }
      }
      
      // If still not found, try to find the media viewer directly
      if (allMediaItems.length === 0) {
        const productMediaViewer = this.productEl.querySelector('.media-viewer');
        if (productMediaViewer) {
          allMediaItems = productMediaViewer.querySelectorAll('.media-viewer__item, li[data-media-id]');
        }
      }
      
      // Also check for product-media container
      if (allMediaItems.length === 0) {
        const productMedia = this.productEl.querySelector('.product-media, #product-media');
        if (productMedia) {
          allMediaItems = productMedia.querySelectorAll('.media-viewer__item, li[data-media-id]');
        }
      }

      if (allMediaItems.length === 0) {
        console.log('Quick Add: No media items found. Product element:', this.productEl);
        console.log('Quick Add: Product element HTML:', this.productEl.innerHTML.substring(0, 500));
        // Try fallback: create from product images if available
        this.createCarouselFromImages();
        return;
      }

      mediaViewer.innerHTML = '';
      dotsContainer.innerHTML = '';

      // Find the current variant's media item index
      const currentVariantItem = Array.from(allMediaItems).find(item => 
        item.classList.contains('is-current-variant')
      );
      const currentVariantIndex = currentVariantItem ? Array.from(allMediaItems).indexOf(currentVariantItem) : 0;

      console.log(`Quick Add: Found ${allMediaItems.length} media items`);

      // Clone and add all media items to the carousel
      allMediaItems.forEach((mediaItem, index) => {
        const clonedItem = mediaItem.cloneNode(true);
        clonedItem.classList.remove('active', 'is-current-variant');
        if (index === currentVariantIndex) {
          clonedItem.classList.add('active');
        }
        
        // Ensure images are loaded and visible
        const images = clonedItem.querySelectorAll('img');
        images.forEach(img => {
          // Force display if hidden
          img.style.display = 'block';
          img.style.width = '100%';
          img.style.height = 'auto';
          img.style.maxWidth = '100%';
          
          // Ensure src is loaded if using data-src
          if (img.dataset.src && !img.src) {
            img.src = img.dataset.src;
          }
          
          // Force loading
          if (img.loading === 'lazy') {
            img.loading = 'eager';
          }
        });
        
        // Ensure deferred-media elements are loaded
        const deferredMedia = clonedItem.querySelectorAll('deferred-media, product-model');
        deferredMedia.forEach(dm => {
          if (dm.loadContent && typeof dm.loadContent === 'function') {
            dm.loadContent();
          }
        });
        
        mediaViewer.appendChild(clonedItem);

        // Create dot button for each media item (only if more than one item)
        if (allMediaItems.length > 1) {
          const dotBtn = document.createElement('div');
          dotBtn.className = `dotbtn ${index === currentVariantIndex ? 'active' : ''}`;
          dotBtn.setAttribute('data-index', index);
          dotBtn.setAttribute('aria-controls', 'quick-add-gallery-viewer');
          dotBtn.innerHTML = `
            <svg xmlns="http://www.w3.org/2000/svg" width="69" height="6" viewBox="0 0 69 6" fill="none">
              <rect width="6" height="6" fill="black"/>
            </svg>
          `;
          dotBtn.addEventListener('click', () => {
            this.scrollToMediaItem(index);
          });
          dotsContainer.appendChild(dotBtn);
        }
      });

      console.log('Quick Add: Carousel items added, initializing scroll handler');
      
      // Initialize scroll handling and scroll to current variant
      this.initMediaCarouselScroll();
      
      // Initialize arrow navigation
      this.initMediaCarouselArrows();
      
      // Scroll to current variant's image after a short delay to ensure layout
      if (currentVariantIndex > 0 && allMediaItems.length > 1) {
        setTimeout(() => {
          this.scrollToMediaItem(currentVariantIndex);
        }, 200);
      }
      
      // Force a layout recalculation to ensure visibility
      setTimeout(() => {
        mediaViewer.style.display = 'flex';
        mediaViewer.offsetHeight; // Force reflow
        console.log('Quick Add: Media viewer items:', mediaViewer.children.length);
      }, 50);
    }

    /**
     * Handles variant change events to update the carousel.
     */
    handleVariantChangeForCarousel(evt) {
      const variant = evt?.detail?.variant || evt?.variant;
      if (!variant) {
        // Try to get variant from variant picker
        const variantPicker = this.querySelector('variant-picker');
        if (variantPicker && variantPicker.variant) {
          this.updateCarouselForVariant(variantPicker.variant);
        }
        return;
      }

      this.updateCarouselForVariant(variant);
    }

    /**
     * Updates the carousel to show the variant's featured image.
     */
    updateCarouselForVariant(variant) {
      const mediaViewer = this.querySelector('.quick-add-media-viewer');
      if (!mediaViewer) {
        console.log('Quick Add: Media viewer not found for variant update');
        return;
      }

      console.log('Quick Add: Variant changed, updating carousel', variant);

      // Get variant's featured media ID
      const variantMediaId = variant.featured_media?.id || null;
      if (!variantMediaId) {
        console.log('Quick Add: No featured media for variant', variant.id);
        // Try to get from product media based on variant
        return;
      }

      // Try to find in current carousel items
      const items = mediaViewer.querySelectorAll('.media-viewer__item');
      let targetIndex = -1;

      items.forEach((item, index) => {
        const itemMediaId = item.dataset.mediaId;
        if (itemMediaId) {
          const mediaIdNum = parseInt(itemMediaId);
          if (mediaIdNum === variantMediaId) {
            targetIndex = index;
          }
        }
      });

      if (targetIndex >= 0) {
        // Scroll to the variant's image
        console.log('Quick Add: Found variant image at index', targetIndex, 'for media ID', variantMediaId);
        setTimeout(() => {
          this.scrollToMediaItem(targetIndex);
          // Update active states
          items.forEach((item, idx) => {
            item.classList.toggle('active', idx === targetIndex);
          });
          const dotBtns = this.querySelectorAll('.quick-add-dots-btn .dotbtn');
          dotBtns.forEach((dot, idx) => {
            dot.classList.toggle('active', idx === targetIndex);
          });
        }, 150);
      } else {
        // If not found, try refreshing carousel
        console.log('Quick Add: Variant image not found, refreshing carousel. Looking for media ID:', variantMediaId);
        setTimeout(() => {
          this.updateMediaCarousel();
          // After refresh, try to find and scroll to variant image
          setTimeout(() => {
            const newItems = mediaViewer.querySelectorAll('.media-viewer__item');
            newItems.forEach((item, index) => {
              const itemMediaId = item.dataset.mediaId;
              if (itemMediaId && parseInt(itemMediaId) === variantMediaId) {
                console.log('Quick Add: Found variant image after refresh at index', index);
                this.scrollToMediaItem(index);
                // Update active states
                newItems.forEach((itm, idx) => {
                  itm.classList.toggle('active', idx === index);
                });
                const dotBtns = this.querySelectorAll('.quick-add-dots-btn .dotbtn');
                dotBtns.forEach((dot, idx) => {
                  dot.classList.toggle('active', idx === index);
                });
              }
            });
          }, 400);
        }, 200);
      }
    }

    /**
     * Initializes arrow navigation for the media carousel.
     */
    initMediaCarouselArrows() {
      const mediaViewer = this.querySelector('.quick-add-media-viewer');
      const prevBtn = this.querySelector('.quick-add-media-ctrl__btn[name="prev"]');
      const nextBtn = this.querySelector('.quick-add-media-ctrl__btn[name="next"]');
      
      if (!mediaViewer) {
        console.log('Quick Add: Media viewer not found for arrows');
        return;
      }
      
      if (!prevBtn || !nextBtn) {
        console.log('Quick Add: Arrow buttons not found', { prevBtn: !!prevBtn, nextBtn: !!nextBtn });
        return;
      }
      
      // Ensure arrows are visible
      prevBtn.style.display = 'flex';
      nextBtn.style.display = 'flex';

      const items = mediaViewer.querySelectorAll('.media-viewer__item');
      if (items.length <= 1) {
        prevBtn.style.display = 'none';
        nextBtn.style.display = 'none';
        return;
      } else {
        // Ensure arrows are visible when there are multiple items
        prevBtn.style.display = 'flex';
        nextBtn.style.display = 'flex';
      }

      const updateArrows = () => {
        const scrollLeft = mediaViewer.scrollLeft;
        const maxScroll = mediaViewer.scrollWidth - mediaViewer.clientWidth;
        
        prevBtn.disabled = scrollLeft <= 0;
        nextBtn.disabled = scrollLeft >= maxScroll - 10; // 10px tolerance
      };

      prevBtn.addEventListener('click', () => {
        const items = mediaViewer.querySelectorAll('.media-viewer__item');
        const currentScroll = mediaViewer.scrollLeft;
        const itemWidth = items[0]?.offsetWidth || mediaViewer.clientWidth;
        const newScroll = Math.max(0, currentScroll - itemWidth);
        mediaViewer.scrollTo({ left: newScroll, behavior: 'smooth' });
      });

      nextBtn.addEventListener('click', () => {
        const items = mediaViewer.querySelectorAll('.media-viewer__item');
        const currentScroll = mediaViewer.scrollLeft;
        const itemWidth = items[0]?.offsetWidth || mediaViewer.clientWidth;
        const maxScroll = mediaViewer.scrollWidth - mediaViewer.clientWidth;
        const newScroll = Math.min(maxScroll, currentScroll + itemWidth);
        mediaViewer.scrollTo({ left: newScroll, behavior: 'smooth' });
      });

      mediaViewer.addEventListener('scroll', updateArrows);
      updateArrows(); // Initial state
    }

    /**
     * Fallback: Create carousel from product images if media-viewer items are not found.
     */
    createCarouselFromImages() {
      const mediaViewer = this.querySelector('.quick-add-media-viewer');
      const dotsContainer = this.querySelector('.quick-add-dots-btn');
      if (!mediaViewer) {
        console.log('Quick Add Fallback: Media viewer not found');
        return;
      }

      // Try to find images in the product element - look for product media
      let images = this.productEl.querySelectorAll('.media-viewer img, .media img, .product-image, img[data-src], img[src]');
      
      // Filter out very small images (likely icons)
      images = Array.from(images).filter(img => {
        const width = img.naturalWidth || img.width || 0;
        const height = img.naturalHeight || img.height || 0;
        return width > 100 && height > 100;
      });
      
      if (images.length === 0) {
        console.log('Quick Add Fallback: No product images found');
        console.log('Quick Add Fallback: Product element:', this.productEl);
        return;
      }

      console.log(`Quick Add Fallback: Found ${images.length} images`);

      mediaViewer.innerHTML = '';
      dotsContainer.innerHTML = '';

      images.forEach((img, index) => {
        const item = document.createElement('li');
        item.className = 'media-viewer__item';
        if (index === 0) item.classList.add('active');
        item.setAttribute('data-media-id', index);
        item.setAttribute('data-index', index);
        
        const mediaDiv = document.createElement('div');
        mediaDiv.className = 'media relative';
        mediaDiv.style.width = '100%';
        mediaDiv.style.paddingTop = '100%';
        mediaDiv.style.position = 'relative';
        
        const clonedImg = img.cloneNode(true);
        clonedImg.style.width = '100%';
        clonedImg.style.height = '100%';
        clonedImg.style.display = 'block';
        clonedImg.style.objectFit = 'contain';
        clonedImg.style.position = 'absolute';
        clonedImg.style.top = '0';
        clonedImg.style.left = '0';
        
        // Ensure image src is set
        if (clonedImg.dataset.src && !clonedImg.src) {
          clonedImg.src = clonedImg.dataset.src;
        }
        if (clonedImg.dataset.srcset) {
          clonedImg.srcset = clonedImg.dataset.srcset;
        }
        
        mediaDiv.appendChild(clonedImg);
        item.appendChild(mediaDiv);
        mediaViewer.appendChild(item);

        // Create dot
        if (images.length > 1) {
          const dotBtn = document.createElement('div');
          dotBtn.className = `dotbtn ${index === 0 ? 'active' : ''}`;
          dotBtn.setAttribute('data-index', index);
          dotBtn.innerHTML = `
            <svg xmlns="http://www.w3.org/2000/svg" width="69" height="6" viewBox="0 0 69 6" fill="none">
              <rect width="6" height="6" fill="black"/>
            </svg>
          `;
          dotBtn.addEventListener('click', () => {
            this.scrollToMediaItem(index);
          });
          dotsContainer.appendChild(dotBtn);
        }
      });

      this.initMediaCarouselScroll();
      
      // Force visibility
      setTimeout(() => {
        mediaViewer.style.display = 'flex';
        mediaViewer.offsetHeight; // Force reflow
        console.log('Quick Add Fallback: Carousel created with', images.length, 'images');
      }, 50);
    }

    /**
     * Scrolls to a specific media item in the carousel.
     * @param {number} index - Index of the media item to scroll to.
     */
    scrollToMediaItem(index) {
      const mediaViewer = this.querySelector('.quick-add-media-viewer');
      if (!mediaViewer) return;

      const items = mediaViewer.querySelectorAll('.media-viewer__item');
      if (items[index]) {
        const itemLeft = items[index].offsetLeft;
        mediaViewer.scrollTo({ left: itemLeft, behavior: 'smooth' });
      }
    }

    /**
     * Initializes scroll event handling for the media carousel.
     */
    initMediaCarouselScroll() {
      const mediaViewer = this.querySelector('.quick-add-media-viewer');
      if (!mediaViewer) return;

      const items = mediaViewer.querySelectorAll('.media-viewer__item');
      if (items.length === 0) return;

      const handleScroll = () => {
        const scrollLeft = mediaViewer.scrollLeft;
        const containerWidth = mediaViewer.clientWidth;
        const scrollCenter = scrollLeft + containerWidth / 2;
        
        // Find which item is currently most visible in the center
        let activeIndex = 0;
        let minDistance = Infinity;
        
        items.forEach((item, index) => {
          const itemLeft = item.offsetLeft;
          const itemWidth = item.offsetWidth;
          const itemCenter = itemLeft + itemWidth / 2;
          const distance = Math.abs(scrollCenter - itemCenter);
          
          if (distance < minDistance) {
            minDistance = distance;
            activeIndex = index;
          }
        });

        // Update active states
        items.forEach((item, index) => {
          item.classList.toggle('active', index === activeIndex);
        });

        const dotBtns = this.querySelectorAll('.quick-add-dots-btn .dotbtn');
        dotBtns.forEach((dot, index) => {
          dot.classList.toggle('active', index === activeIndex);
        });
      };

      mediaViewer.addEventListener('scroll', handleScroll);
      
      // Initial check after a short delay to ensure layout is complete
      setTimeout(() => {
        handleScroll();
      }, 100);
    }

    /**
     * Builds the markup for the drawer content element.
     */
    updateContent() {
      let weightElem = this.getElementHtml('.product-info__weight');
      let title = this.getElementHtml('.product-title').trim();
      let subtitle = this.getElementHtml('.subTitle').trim(); 
      let vendor = this.getElementHtml(".product-vendor").trim();
      if (weightElem && weightElem.length > 0) {
        weightElem = `<div class="product-info__weight text-sm mt-2">${weightElem}</div>`;
      }

      this.content.innerHTML = `
        <div class="quick-add-infos grid mb-8">
          <div class="product-title">
              <a class="h6 js-prod-link" href="${this.productUrl}">
                <p class="quickviewvendor">${vendor}</p>
                <h1 class="quickviewtitle-two">${title}</h1>
                <p class="quickviewtitle-one">${subtitle}</p>
              </a>
          </div>
          <div class="quick-add-media-gallery relative">
            <div class="quick-add-info__media-viewer-wrapper relative">
              <ul class="quick-add-media-viewer flex" role="list" tabindex="0"></ul>
              <div class="quick-add-dots-btn"></div>
              <div class="quick-add-media-ctrl no-js-hidden">
                <button type="button" class="quick-add-media-ctrl__btn tap-target vertical-center btn" name="prev" aria-controls="quick-add-gallery-viewer" disabled>
                  <span class="visually-hidden">Previous</span>
                  <svg class="icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
                <button type="button" class="quick-add-media-ctrl__btn tap-target vertical-center btn" name="next" aria-controls="quick-add-gallery-viewer">
                  <span class="visually-hidden">Next</span>
                  <svg class="icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>

          <div class="quick-add-info__details md:hidden"></div>
        </div>
         
        <div class="product-options">
          ${this.getElementHtml('.product-options')}
        </div>
        <div class="product-backorder">
          ${this.getElementHtml('.product-backorder')}
        </div>
        
        <div class="product-price">
            <a href="${this.productUrl}" id="prodlink" class="view_linkbtn">לפרטים נוספים</a><br/>
            ${this.getElementHtml('.product-price')}
        </div>
         
         <div class="text-theme-light text-sm mt-4" style="display:none;">
          <a id="quickprod_link" data-url="${this.productUrl}" href="${this.productUrl}" class="link js-prod-link quick-pdplink">
            ${theme.strings.viewDetails}
          </a>
        </div>
        <p>&nbsp;</p>
      `;

      this.classList.remove('is-loading');
      this.content.classList.remove('drawer__content--out');
    }

    /**
     * Updates the Quick Add drawer form (buy buttons).
     */
    updateForm() {
      const productForm = this.productEl.querySelector('product-form');
      this.footer.classList.remove('quick-add__footer-message');

      if (productForm) {
        this.form.innerHTML = productForm.innerHTML;
        this.form.init();

        if (Shopify && Shopify.PaymentButton) {
          Shopify.PaymentButton.init();
        }
      } else {
        const signUpForm = this.productEl.querySelector('.product-signup');
        if (signUpForm) {
          this.form.innerHTML = signUpForm.innerHTML;
        } else {
          this.footer.classList.add('quick-add__footer-message');
          this.form.innerHTML = `
            <div class="alert quick-add__alert bg-info-bg text-info-text">
              <div class="flex">
                <div>
                  <svg class="icon icon--price_tag" width="32" height="32" viewBox="0 0 16 16" aria-hidden="true" focusable="false" role="presentation">
                    <path fill="currentColor" d="M7.59 1.34a1 1 0 01.7-.29h5.66a1 1 0 011 1v5.66a1 1 0 01-.3.7L7.6 15.5a1 1 0 01-1.42 0L.52 9.83a1 1 0 010-1.42l7.07-7.07zm6.36 6.37l-7.07 7.07-5.66-5.66L8.3 2.05h5.66v5.66z" fill-rule="evenodd"/>
                    <path fill="currentColor" d="M9.7 6.3a1 1 0 101.42-1.42 1 1 0 00-1.41 1.41zM9 7a2 2 0 102.83-2.83A2 2 0 009 7z" fill-rule="evenodd"/>
                  </svg>
                </div>
                <div>
                  <p class="text-h6 font-bold mb-2">${theme.strings.awaitingSale}</p>
                  <a class="link js-prod-link" href="${this.productUrl}">${theme.strings.viewDetails}</a>
                </div>
              </div>
            </div>`;
        }
      }

      this.footer.classList.remove('drawer__footer--out');
    }

    /**
     * Gets the innerHTML of an element within the product element.
     * @param {string} selector - CSS selector for the element.
     * @returns {?string}
     */
    getElementHtml(selector) {
      const el = this.productEl.querySelector(selector);
      return el ? el.innerHTML : '';
    }

    /**
     * Shows an "Added to cart" message in the drawer.
     */
    addedToCart() {
      if (this.notification) {
        setTimeout(() => {
          this.notification.hidden = false;
        }, 300);

        setTimeout(() => {
          this.notification.hidden = true;
        }, this.notification.dataset.visibleFor);
      }
    }
  }

  customElements.define('quick-add-drawer', QuickAddDrawer);
}
